import React, { useEffect, useRef, useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import x from '../../assets/icons/close.svg'
import photo from '../../assets/icons/phot.svg'
import showAllCategories from '../../apis/showAllCategories'
import showSubCategories from '../../apis/showSubCategories'
import getCountries from '../../apis/getCountries'
import editCard from '../../apis/editCard'
import showSingelCard from '../../apis/showSingelCard'

const EditCard = ({ active, close, cardId, updateCard }) => {
  const popupref = useRef(null)
  const [error, setError] = useState('')
  const [card, setCard] = useState({})
  const [cardData, setCardData] = useState({})
  const [imageFile, setImageFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState([])
  const [countries, setCountries] = useState([])

  const fetchItems = useCallback(async () => {
    try {
      const cardData = await showSingelCard(cardId)

      const updatedCardData = {
        ...cardData,
        category_id: cardData.categories,
      }
      delete updatedCardData.categories

      setCard(updatedCardData)
      setCardData({
        title: updatedCardData.title,
        description: updatedCardData.description,
        categoryIds: updatedCardData.category_id || [],
        countryId: updatedCardData.country_id || '',
        countryName: updatedCardData.country_name || '',
        is_global:
          updatedCardData.is_global || updatedCardData.country_id === ''
            ? 1
            : 0,
      })

      setPreview(updatedCardData.image)
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }, [cardId])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    setImageFile(file)
    setPreview(URL.createObjectURL(file))
  }

  const handleDragOver = (e) => {
    e.preventDefault()
  }

  const handleDrop = (e) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    setImageFile(file)
    setPreview(URL.createObjectURL(file))
  }

  const clearFiles = (e) => {
    e.preventDefault()
    setPreview(null)
    setImageFile(null)
  }

  const handelEditCard = async (e) => {
    e.preventDefault()

    if (!card.image && !imageFile) {
      setError('add an image')
      return
    }

    const formData = new FormData()
    let isUpdated = false

    if (cardData.title !== card.title) {
      formData.append('title', cardData.title)
      isUpdated = true
    }

    if (cardData.description !== card.description) {
      formData.append('description', cardData.description)
      isUpdated = true
    }

    if (cardData.is_global === 1) {
      formData.append('is_global', cardData.is_global)
      isUpdated = true
    }
    if (cardData.is_global === 0 && cardData.countryId !== card.country_id) {
      formData.append('country_id', cardData.countryId)
      isUpdated = true
    }

    if (imageFile) {
      formData.append('images', imageFile)
      isUpdated = true
    }

    if (cardData.categoryIds && cardData.categoryIds.length > 0) {
      formData.append('category_id', `[${cardData.categoryIds}]`)
      isUpdated = true // Treat as an update
    }
    if (isUpdated) {
      try {
        setLoading(true)
        await editCard(card.id, formData) // Make the API call

        // Update the card data in the parent state
        updateCard({
          ...card,
          ...cardData,
          image: imageFile ? URL.createObjectURL(imageFile) : card.image,
        })

        // Reset form data
        setCardData({
          title: '',
          description: '',
          categoryIds: [],
          countryId: '',
        })
        setImageFile(null)
        setPreview(null)

        close() // Close the edit modal
      } catch (error) {
        setError(error.response.data.message)
      } finally {
        setLoading(false)
      }
    } else {
      close() // Close the modal if no updates are made
    }
  }

  const handelInputChange = (e) => {
    const { name, value } = e.target
    setCardData((prevData) => ({ ...prevData, [name]: value }))
    setError('')
  }

  const handelGlobalChange = (e) => {
    setCardData((prevData) => ({
      ...prevData,
      is_global: prevData.is_global === 1 ? 0 : 1,
      countryId: prevData.is_global === 1 ? '' : cardData.countryId,
    }))
  }

  const fetchCategoriesAndCountries = useCallback(async () => {
    try {
      const allCountries = await getCountries()
      setCountries(allCountries)

      const allCategories = await showAllCategories()
      const categoriesWithSubCategories = await Promise.all(
        allCategories.map(async (category) => {
          const subCategories = await showSubCategories(category.id)
          return { ...category, subCategories }
        })
      )
      const initialCountry = allCountries.find(
        (country) => country.name === card.country_name
      )
      if (initialCountry) {
        setCardData((prevData) => ({
          ...prevData,
          countryId: initialCountry.id,
          countryName: initialCountry.name,
        }))
      }
      setCategories(categoriesWithSubCategories)
    } catch (error) {
      console.error('Failed to fetch categories and countries', error)
    }
  }, [card.country_name])

  useEffect(() => {
    fetchCategoriesAndCountries()
  }, [fetchCategoriesAndCountries])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupref.current && !popupref.current.contains(event.target)) {
        close()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [popupref, close])

  useEffect(() => {
    if (active) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }, [active])

  const handleCategoryChange = (id, hasSubCategories) => {
    if (hasSubCategories) return

    setCardData((prevData) => {
      const newCategoryIds = prevData.categoryIds.includes(id)
        ? prevData.categoryIds.filter((categoryId) => categoryId !== id)
        : [...prevData.categoryIds, id]
      return { ...prevData, categoryIds: newCategoryIds }
    })
  }

  const handleCountryChange = (e) => {
    setCardData({
      ...cardData,
      countryId: e.target.value,
      countryName: countries.map((country) => {
        if (Number(country.id) === Number(e.target.value)) {
          return country.name
        }
        return null
      }),
      country_name: countries.map((country) => {
        if (Number(country.id) === Number(e.target.value)) {
          return country.name
        }
        return null
      }),
    })
  }

  return (
    <motion.div
      className="fixed inset-0 bg-overlay/30 w-screen z-40 h-screen grid place-items-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        ref={popupref}
        initial={{ opacity: 0, y: -100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ duration: 0.3 }}
        className="w-1/2 min-h-10 overflow-hidden bg-white rounded-lg flex flex-col justify-center items-center"
      >
        <form onSubmit={handelEditCard} className="w-full">
          <div className="w-full px-8 py-5 flex flex-col gap-8">
            <div className="flex border-b-[1px] pb-4 justify-between border-b-darkBlue w-full">
              <div>Edit Card</div>
              <img src={x} onClick={close} className="cursor-pointer" alt="" />
            </div>
            <div className="flex gap-4 justify-center items-center">
              <div className="w-1/2 flex flex-col gap-4">
                <div className="rounded-xl p-4 border-[1px] h-1/3 border-b-darkBlue">
                  <label
                    htmlFor="image"
                    className="w-full cursor-pointer rounded-xl relative p-4"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <input
                      name="image"
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden w-full focus:outline-none"
                    />
                    {preview ? (
                      <div className="w-full absolute top-2 right-2 z-10 flex justify-end items-center">
                        <img
                          onClick={clearFiles}
                          className="cursor-pointer"
                          src={x}
                          alt=""
                        />
                      </div>
                    ) : (
                      <div className="flex w-full justify-center items-center">
                        Choose or Drop a File
                      </div>
                    )}
                    <img
                      src={preview || photo}
                      alt="Preview"
                      className={`mt-4 w-full h-28 ${
                        preview ? 'object-contain' : 'object-contain'
                      }`}
                    />
                  </label>
                </div>
                <div className="w-full h-56 overflow-auto rounded-xl px-4 py-3 border-[1px] border-b-darkBlue">
                  <div className="flex flex-col gap-4">
                    {categories.map((cat) => (
                      <React.Fragment key={cat.id}>
                        <div className="text-gray font-bold">{cat.title}</div>
                        <div className="flex flex-wrap gap-2 border-b border-gray-300 pb-2 mb-2">
                          {cat.subCategories.length > 0 ? (
                            cat.subCategories.map((sub) => (
                              <div
                                key={sub.id}
                                className={`p-2 border rounded-lg cursor-pointer ${
                                  cardData.categoryIds?.includes(sub.id)
                                    ? 'bg-blueButton text-white'
                                    : 'text-gray-700 border-gray-300'
                                }`}
                                onClick={() =>
                                  handleCategoryChange(sub.id, false)
                                }
                              >
                                - {sub.title}
                              </div>
                            ))
                          ) : (
                            <div
                              className={`p-2 border rounded-lg cursor-pointer ${
                                cardData.categoryIds?.includes(cat.id)
                                  ? 'bg-blueButton text-white'
                                  : 'text-gray-700 border-gray-300'
                              }`}
                              onClick={() =>
                                handleCategoryChange(cat.id, false)
                              }
                            >
                              {cat.title}
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
              <div className="w-1/2 flex flex-col gap-4">
                <div className="w-full rounded-xl px-4 py-3 border-[1px] border-b-darkBlue">
                  <input
                    value={cardData.title}
                    required
                    onChange={handelInputChange}
                    className="w-full focus:outline-none"
                    placeholder="Card Name"
                    name="title"
                    id="title"
                  />
                </div>
                <div className=" flex justify-between">
                  <label htmlFor="is_global">Is global?</label>
                  <input
                    name="is_global"
                    id="is_global"
                    type="checkbox"
                    checked={cardData.is_global === 1}
                    onChange={handelGlobalChange}
                  ></input>
                </div>
                {cardData.is_global === 0 && (
                  <div className="w-full rounded-xl px-4 py-3 border-[1px] border-b-darkBlue">
                    <select
                      className="focus:outline-none w-full text-gray"
                      onChange={handleCountryChange}
                      value={cardData.countryId || 'all'}
                      name="countryName"
                      id="countryName"
                    >
                      <option
                        disabled={true}
                        className="disabled:text-gray text-blueButton"
                        value="all"
                      >
                        Select Country
                      </option>
                      {countries?.map((country) => (
                        <option
                          className="text-blueButton"
                          key={country.id}
                          value={country.id}
                        >
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="w-full h-80 rounded-xl px-4 py-3 border-[1px] border-b-darkBlue flex flex-col justify-between">
                  <textarea
                    value={cardData.description}
                    required
                    name="description"
                    id="description"
                    onChange={handelInputChange}
                    className="w-full h-full focus:outline-none"
                    placeholder="Description"
                  />
                </div>
              </div>
            </div>
          </div>
          {error && (
            <div className=" w-full flex justify-center py-3 items-center">
              <span className=" bg-red/20 px-3 py-1 text-red rounded">
                {error}
              </span>
            </div>
          )}
          <button
            disabled={loading}
            className="disabled:bg-darkBlue/50 bg-blueButton text-white text-center p-4 w-full"
          >
            Edit
          </button>
        </form>
      </motion.div>
    </motion.div>
  )
}

export default EditCard
